import type { useTranslation } from "react-i18next";

import {
  ForgejoRequestError,
  HTTP_BAD_GATEWAY,
  HTTP_UNAUTHORIZED,
  HTTP_UNPROCESSABLE_ENTITY,
  HttpError,
} from "./api";

type TFn = ReturnType<typeof useTranslation>["t"];

// Зеркала строк из backend/modules/repo_host/providers/forgejo/connections/details.py;
// перечень отказа 422 verify/PUT — enum в openapi.py.
export const FORGEJO_INVALID_BODY_DETAIL = "request body must be a JSON object";
export const FORGEJO_INVALID_HOST_URL_DETAIL =
  "host_url must be an http(s) URL without credentials, query or fragment";
export const FORGEJO_INVALID_PAT_FIELD_DETAIL =
  "pat must be a non-empty string of printable ASCII characters without spaces";
export const FORGEJO_INVALID_BOT_USERNAME_DETAIL =
  "bot username must be a valid forgejo user name";

export type ForgejoFormErrorTarget = "hostUrl" | "pat" | "form";

export interface ForgejoFormError {
  readonly target: ForgejoFormErrorTarget;
  readonly message: string;
}

// Текст отказа без привязки к полю: 401 — неверный PAT, 502 — Forgejo
// недоступен, остальное — общий текст с сообщением ошибки.
export function describeForgejoError(err: unknown, t: TFn): string {
  if (err instanceof HttpError) {
    if (err.status === HTTP_UNAUTHORIZED) {
      return t("connections.invalidPat");
    }
    if (err.status === HTTP_BAD_GATEWAY) {
      return t("connections.upstreamUnavailable");
    }
  }
  const message = err instanceof Error ? err.message : String(err);
  return t("connections.genericError", { message });
}

// Куда вывести отказ verify/PUT: `422` с известным `detail` — на поле,
// которое сервер счёл неверным; прочий `422` — в Alert формы.
export function describeForgejoFormError(
  err: unknown,
  t: TFn,
): ForgejoFormError {
  if (err instanceof HttpError && err.status === HTTP_UNPROCESSABLE_ENTITY) {
    const detail = err instanceof ForgejoRequestError ? err.detail : null;
    if (detail === FORGEJO_INVALID_HOST_URL_DETAIL) {
      return {
        target: "hostUrl",
        message: t("connections.forgejo.errors.invalidHostUrl"),
      };
    }
    if (detail === FORGEJO_INVALID_PAT_FIELD_DETAIL) {
      return {
        target: "pat",
        message: t("connections.forgejo.errors.invalidPat"),
      };
    }
    return {
      target: "form",
      message: t("connections.forgejo.errors.invalidBody"),
    };
  }
  return { target: "form", message: describeForgejoError(err, t) };
}
